import { createServerFn } from "@tanstack/react-start";

export type TranslatableFields = Record<string, string>;

export interface ContentTranslationArgs {
  contentType: string;
  contentId: string;
  languageCode: string;
  fields: TranslatableFields;
}

export interface ContentTranslationResult {
  success: boolean;
  fields?: TranslatableFields;
  error?: string;
}

const LANGUAGES: Record<string, string> = {
  no: "Norwegian (bokmal)",
  "zh-Hant": "Traditional Chinese, using Taiwan and Hong Kong wording, never simplified characters",
};

const MAX_FIELDS = 40;
const MAX_CHARS = 24000;

/**
 * Translate a handful of text fields belonging to one piece of content
 * (weekly brief, daily recap, podcast show...). Cached per language so the
 * first reader pays the wait and everybody after gets it instantly.
 */
export const translateContent = createServerFn({ method: "POST" })
  .inputValidator((input: ContentTranslationArgs) => {
    const languageCode = LANGUAGES[input?.languageCode ?? ""] ? input.languageCode : "";
    if (!languageCode) throw new Error("Unsupported language");
    const contentType = String(input.contentType || "").slice(0, 40);
    const contentId = String(input.contentId || "").slice(0, 120);
    if (!contentType || !contentId) throw new Error("Missing content reference");

    const fields: TranslatableFields = {};
    let total = 0;
    for (const [key, value] of Object.entries(input.fields ?? {})) {
      if (typeof value !== "string") continue;
      const text = value.trim();
      if (!text) continue;
      if (Object.keys(fields).length >= MAX_FIELDS) break;
      total += text.length;
      if (total > MAX_CHARS) break;
      fields[key.slice(0, 60)] = text;
    }
    if (Object.keys(fields).length === 0) throw new Error("Nothing to translate");

    return { contentType, contentId, languageCode, fields };
  })
  .handler(async ({ data }): Promise<ContentTranslationResult> => {
    const { contentType, contentId, languageCode, fields } = data;
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: cached } = await supabaseAdmin
      .from("content_translations")
      .select("fields")
      .eq("content_type", contentType)
      .eq("content_id", contentId)
      .eq("language_code", languageCode)
      .maybeSingle();

    const cachedFields = (cached?.fields ?? {}) as TranslatableFields;
    const missing = Object.keys(fields).filter((key) => typeof cachedFields[key] !== "string");
    if (missing.length === 0) return { success: true, fields: cachedFields };

    const { geminiChat } = await import("./ai.server");
    const payload: TranslatableFields = {};
    for (const key of missing) payload[key] = fields[key]!;

    try {
      const raw = await geminiChat(
        [
          {
            role: "system",
            content:
              `You translate editorial copy for a technology news site into ${LANGUAGES[languageCode]}. ` +
              "Keep the meaning, tone and any HTML tags exactly as they are. " +
              "Keep names, tickers and numbers unchanged. " +
              "Return ONLY a JSON object with the same keys and translated values.",
          },
          { role: "user", content: JSON.stringify(payload) },
        ],
        { temperature: 0.2, maxTokens: 8192, responseFormat: { type: "json_object" } },
      );

      const parsed = JSON.parse(raw.replace(/```json/gi, "").replace(/```/g, "").trim()) as
        | Record<string, unknown>
        | null;

      const merged: TranslatableFields = { ...cachedFields };
      for (const key of missing) {
        const value = parsed?.[key];
        if (typeof value === "string" && value.trim()) merged[key] = value.trim();
      }

      await supabaseAdmin.from("content_translations").upsert(
        {
          content_type: contentType,
          content_id: contentId,
          language_code: languageCode,
          fields: merged as never,
        },
        { onConflict: "content_type,content_id,language_code" },
      );

      return { success: true, fields: merged };
    } catch (error) {
      console.error("translateContent failed", error);
      return { success: false, error: "Translation unavailable" };
    }
  });
